import React, { useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Helmet } from "react-helmet";
import AdminLayout from "../components/AdminLayout";
import { BASE_URLS } from "../../config/urls";
import { apiFetch } from "../../utils/api";
import "../styles/admin_service_users.css";
import {BtnSpinner} from "../../components/fields/BtnSpinner";

type Role = {
    code: string;   // системное имя/код роли (например, ROLE_ADMIN_ORDERS)
    name: string;   // человекочитаемое имя роли (например, "Администратор заказов")
};

type ServiceUser = {
    id: number;
    name: string;
    email: string;
    roles: string[]; // массив кодов ролей (Role.code)
};

type ModalMode = "create" | "edit";

const ServiceUsersAdminPage: React.FC = () => {
    const [serviceUsers, setServiceUsers] = useState<ServiceUser[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<ModalMode>("create");
    const [currentUser, setCurrentUser] = useState<ServiceUser | null>(null);

    // Поля формы модалки
    const [formName, setFormName] = useState("");
    const [formEmail, setFormEmail] = useState("");
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);   // ошибка по email (create)
    const [rolesError, setRolesError] = useState<string | null>(null); // ошибка по ролям

    // Загрузка ролей и пользователей
    useEffect(() => {
        fetchRoles();
        fetchServiceUsers();
    }, []);

    function fetchRoles() {
        apiFetch(`${BASE_URLS.api}/admin/service-users/roles`, {
            method: "GET",
            headers: {
                Accept: "application/json",
            },
        })
            .then((resp) => resp.json())
            .then((data: Role[]) => {
                setRoles(data);
            })
            .catch((e) => console.error(e));
    }

    function fetchServiceUsers() {
        setIsLoading(true);
        apiFetch(`${BASE_URLS.api}/admin/service-users`, {
            method: "GET",
            headers: {
                Accept: "application/json",
            },
        })
            .then((resp) => resp.json())
            .then((data: ServiceUser[]) => {
                setServiceUsers(data);
            })
            .catch((e) => {
                console.error(e);
                setServiceUsers([]);
            })
            .finally(() => setIsLoading(false));
    }

    // Открыть модалку для создания
    function openCreateModal() {
        setModalMode("create");
        setCurrentUser(null);
        setFormName("");
        setFormEmail("");
        setSelectedRoles([]);
        setFormError(null);
        setRolesError(null);
        setIsModalOpen(true);
        document.body.style.overflow = "hidden";
    }

    // Открыть модалку для редактирования ролей
    function openEditModal(user: ServiceUser) {
        setModalMode("edit");
        setCurrentUser(user);
        setFormName(user.name);
        setFormEmail(user.email);
        setSelectedRoles(user.roles || []);
        setFormError(null);
        setRolesError(null);
        setIsModalOpen(true);
        document.body.style.overflow = "hidden";
    }

    function closeModal() {
        setIsModalOpen(false);
        document.body.style.overflow = "auto";
        setIsSaving(false);
        setFormError(null);
        setRolesError(null);
    }

    // Переключение роли в модалке
    function toggleRole(code: string) {
        setSelectedRoles((prev) => {
            const next = prev.includes(code)
                ? prev.filter((r) => r !== code)
                : [...prev, code];

            // если пользователь начал менять роли — можно очистить ошибку
            if (rolesError && next.length > 0) {
                setRolesError(null);
            }

            return next;
        });
    }

    // Сохранение (создание/редактирование)
    function handleModalSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (isSaving) return;

        setFormError(null);
        setRolesError(null);

        // 🔴 Валидация: должна быть выбрана хотя бы одна роль
        if (selectedRoles.length === 0) {
            setRolesError("Выберите хотя бы одну роль");
            return;
        }

        setIsSaving(true);

        if (modalMode === "create") {
            const payload = {
                name: formName,
                email: formEmail,
                roles: selectedRoles,
            };

            apiFetch(`${BASE_URLS.api}/admin/service-users`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(payload),
            })
                .then(async (resp) => {
                    if (!resp.ok) {
                        let message = "Не удалось создать сервисного пользователя";

                        try {
                            const data = await resp.json();
                            if (data && (data.message || data.error)) {
                                message = data.message || data.error;
                            }
                        } catch {
                            // оставляем дефолтное сообщение
                        }

                        throw new Error(message);
                    }

                    return resp.json();
                })
                .then(() => {
                    fetchServiceUsers();
                    closeModal();
                })
                .catch((e) => {
                    console.error(e);
                    // здесь мы хотим показать ошибку именно под email
                    setFormError("Пользователь с таким email уже существует");
                    setIsSaving(false);
                });
        } else if (modalMode === "edit" && currentUser) {
            const payload = {
                roles: selectedRoles,
            };

            apiFetch(`${BASE_URLS.api}/admin/service-users/${currentUser.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(payload),
            })
                .then(async (resp) => {
                    if (!resp.ok) {
                        let message = "Не удалось обновить роли пользователя";

                        try {
                            const data = await resp.json();
                            if (data && (data.message || data.error)) {
                                message = data.message || data.error;
                            }
                        } catch {
                            // оставляем дефолтное сообщение
                        }

                        throw new Error(message);
                    }

                    return resp.json();
                })
                .then(() => {
                    fetchServiceUsers();
                    closeModal();
                })
                .catch((e) => {
                    console.error(e);
                    // Для edit можно показать общий текст (если надо)
                    setFormError(e.message);
                    setIsSaving(false);
                });
        }
    }

    // Удаление пользователя
    function handleDelete(user: ServiceUser) {
        const ok = window.confirm(
            `Удалить сервисного пользователя "${user.name}" (${user.email})?`
        );
        if (!ok) return;

        apiFetch(`${BASE_URLS.api}/admin/service-users/${user.id}`, {
            method: "DELETE",
            headers: {
                Accept: "application/json",
            },
        })
            .then(() => {
                fetchServiceUsers();
            })
            .catch((e) => console.error(e));
    }

    return (
        <AdminLayout>
            <HelmetProvider>
                <Helmet title="Сервисные пользователи" />
            </HelmetProvider>

            <div className="page_content service-users-page">
                <section className="admin-card" style={{ marginTop: 90 }}>
                    <h1 className="admin-heading">Сервисные пользователи</h1>
                    <p className="admin-subtitle">
                        Управление сервисными пользователями и их ролями.
                    </p>

                    <div className="service-users-header">
                        <button
                            type="button"
                            className="admin-button-primary"
                            onClick={openCreateModal}
                        >
                            + Добавить сервисного пользователя
                        </button>
                    </div>

                    <div className="admin-table-wrapper">
                        <table className="admin_table service-users-table">
                            <thead>
                            <tr>
                                <th style={{ width: "30%" }}>Имя</th>
                                <th style={{ width: "40%" }}>Роли</th>
                                <th style={{ width: "30%" }}>Действия</th>
                            </tr>
                            </thead>
                            <tbody>
                            {isLoading && (
                                <tr>
                                    <td colSpan={3} className="admin-table-loading">
                                        <span className="admin-spinner" />
                                        Загрузка сервисных пользователей...
                                    </td>
                                </tr>
                            )}

                            {!isLoading &&
                                serviceUsers.map((user) => (
                                    <tr key={user.id}>
                                        <td>
                                            <div className="service-user-name">
                                                {user.name}
                                            </div>
                                            <div className="service-user-email">
                                                {user.email}
                                            </div>
                                        </td>
                                        <td>
                                            {user.roles && user.roles.length > 0 ? (
                                                user.roles.map((roleCode) => {
                                                    const role = roles.find(
                                                        (r) => r.code === roleCode
                                                    );
                                                    const label =
                                                        role?.name || roleCode;
                                                    return (
                                                        <div
                                                            key={roleCode}
                                                            className="admin-role-badge"
                                                        >
                                                            {label}
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <span className="admin-muted">
                                                    Нет ролей
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="service-users-actions">
                                                <button
                                                    type="button"
                                                    className="admin-button-secondary admin-button-secondary--ghost"
                                                    onClick={() => openEditModal(user)}
                                                >
                                                    Изменить
                                                </button>
                                                <button
                                                    type="button"
                                                    className="admin-button-secondary admin-button-secondary--danger"
                                                    onClick={() => handleDelete(user)}
                                                >
                                                    Удалить
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}

                            {!isLoading && serviceUsers.length === 0 && (
                                <tr>
                                    <td colSpan={3} className="admin-table-empty">
                                        Сервисные пользователи не найдены. Добавьте нового
                                        пользователя.
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Модалка добавления / редактирования */}
                {isModalOpen && (
                    <div className="service-user-modal-overlay" onClick={closeModal}>
                        <div
                            className="service-user-modal"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h2 className="service-user-modal-title">
                                {modalMode === "create"
                                    ? "Добавить сервисного пользователя"
                                    : "Изменить роли пользователя"}
                            </h2>

                            <form onSubmit={handleModalSubmit} className="service-user-form">
                                {modalMode === "create" && (
                                    <>
                                        <div className="admin-field">
                                            <div className="admin_title">Имя</div>
                                            <input
                                                type="text"
                                                className="admin-input"
                                                value={formName}
                                                onChange={(e) => setFormName(e.target.value)}
                                                required
                                            />
                                        </div>

                                        <div className="admin-field">
                                            <div className="admin_title">Адрес почты</div>
                                            <input
                                                type="email"
                                                className="admin-input"
                                                value={formEmail}
                                                onChange={(e) => {
                                                    setFormEmail(e.target.value);
                                                    if (formError) {
                                                        setFormError(null);
                                                    }
                                                }}
                                                required
                                            />
                                            {formError && (
                                                <div className="admin-error-text">
                                                    {formError}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                <div className="admin-field">
                                    <div className="admin_title">Тип (роли)</div>
                                    <div className="service-user-roles-list">
                                        {roles.map((role) => (
                                            <label
                                                key={role.code}
                                                className="service-user-role-item"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRoles.includes(
                                                        role.code
                                                    )}
                                                    onChange={() =>
                                                        toggleRole(role.code)
                                                    }
                                                />
                                                <span>{role.name}</span>
                                            </label>
                                        ))}

                                        {roles.length === 0 && (
                                            <div className="admin-muted">
                                                Роли не загружены.
                                            </div>
                                        )}
                                    </div>
                                    {rolesError && (
                                        <div className="admin-error-text">
                                            {rolesError}
                                        </div>
                                    )}
                                </div>

                                <div className="service-user-modal-actions">
                                    <button
                                        type="button"
                                        className="admin-button-secondary admin-button-secondary--ghost"
                                        onClick={closeModal}
                                        disabled={isSaving}
                                    >
                                        Отмена
                                    </button>
                                    <button
                                        type="submit"
                                        className="admin-button-primary"
                                        disabled={isSaving}
                                    >
                                        {isSaving ? (
                                            <BtnSpinner/>
                                        ) : modalMode === "create" ? (
                                            "Добавить"
                                        ) : (
                                            "Сохранить"
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default ServiceUsersAdminPage;
